import OfferSvg from '@library/images/common/offer.svg'
import dayjs from 'dayjs'
import { Button, Divider, DownOutlined, Popover, TagsFilled, Tooltip } from 'gokwik-ui-kit'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { getUserDetails, isMerchantSelector } from '@store/user/selectors'
import { useSelector } from 'react-redux'
import { OFFER_CATEGORY } from '../constants'
import { getCardLabel } from './offer.createEdit.service'

interface SummaryProps {
    currencySymbol: string
    createdBy?: string
}

export interface SummaryRef {
    updateChildState: (values: Record<string, any>) => void
}

const Summary = forwardRef<SummaryRef, SummaryProps>(({ currencySymbol, createdBy }, ref) => {
    const [values, setValues] = useState<Record<string, any>>({})
    const [tncExpand, setTncExpand] = useState<boolean>(false)
    const isMerchantUser = useSelector(isMerchantSelector)
    const userData = useSelector(getUserDetails)

    useImperativeHandle(ref, () => ({
        updateChildState: (newValues: Record<string, any>) => {
            setValues(newValues)
        },
    }))

    if (!values) return null

    const formatArrayValue = (value: any[], key: string) => {
        if (!Array.isArray(value) || !value.length) return '-'
        const joinedValue = value.join(', ')
        return {
            display: `${value.length} ${key}${value.length > 1 ? 's' : ''}`,
            tooltip: joinedValue,
        }
    }

    const formatBankCodes = (value: any[]) => {
        if (!Array.isArray(value) || !value.length) return '-'

        return {
            display: `${value.length} Bank${value.length > 1 ? 's' : ''}`,
            title: 'Tenures',
            popover: (
                <div className='max-h-40 flex flex-col gap-y-2 overflow-y-scroll'>
                    {value.map((bank, i, arr) => (
                        <div key={i}>
                            <h4 className='my-0'>{bank.bank}</h4>
                            {Object.entries(bank.tenures).map(([method, months]) => (
                                <div key={method}>
                                    <h5 className='my-0'>{getCardLabel(method)}</h5>
                                    <ul className='my-0 pl-4'>
                                        {(months as string[]).map((month) => (
                                            <li key={month} className='font-light'>
                                                {month.match(/.*\_(\d+)/)?.[1]} Months
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                            {i < arr.length - 1 ? <Divider className='my-1' dashed /> : null}
                        </div>
                    ))}
                </div>
            ),
        }
    }

    const formatValue = (key: string, value: any) => {
        if (value === undefined || value === null) return '-'

        switch (key) {
            case 'period':
                return {
                    display: value?.map((date: dayjs.Dayjs) => date.format('DD/MM/YY')).join(' - ') || '-',
                    tooltip: value?.map((date: dayjs.Dayjs) => date.format('DD MMM YYYY HH:mm')).join(' - ') || '-',
                }

            case 'amount':
            case 'max_discount_amount':
            case 'min_cart':
            case 'max_cart':
                return !isNaN(value) ? `${currencySymbol}${value}` : '-'

            case 'percentage':
                return value ? `${value}%` : '-'

            case 'offer_category':
                switch (value) {
                    case OFFER_CATEGORY.CASHBACK:
                        return 'Cashback'

                    case OFFER_CATEGORY.PAYMENT_PARTNER:
                        return 'Instant Off'

                    case OFFER_CATEGORY.NO_COST_EMI:
                        return 'No Cost EMI'
                }

            case 'view_in_listing':
            case 'override_badge_text':
            case 'limit_discount_amount':
            case 'auto_apply':
                return value ? 'Yes' : 'No'

            case 'payment_method_type':
                const methodMap = {
                    cards: 'Cards',
                    emi: 'EMI',
                    wallet: 'Wallets',
                    upi: 'UPI',
                }
                return methodMap[value] || value

            case 'cards':
                return formatArrayValue(value, 'Card')
            case 'emis':
                return formatArrayValue(value, 'EMI Method')
            case 'valid_banks':
                return formatArrayValue(value, 'Bank')
            case 'bins':
                return formatArrayValue(value, 'BIN')

            case 'valid_bank_codes':
                return formatBankCodes(value)

            case 'show_valid_banks':
                return value === 'all' ? 'All Banks' : 'Specific Banks'

            case 'merchants':
                if (!value || typeof value !== 'object') return '-'
                const enabled = Object.entries(value).filter((entry) => entry[1])
                const enabledCount = enabled.length
                const totalCount = userData?.allowed_merchants?.length
                return {
                    display: `${enabledCount} of ${totalCount}`,
                    popover: (
                        <div>
                            {enabled.map((entry, i) => (
                                <p key={i}>{entry[0]}</p>
                            ))}
                        </div>
                    ),
                    title: 'Merchants',
                }

            default:
                if (typeof value === 'string' && value.length > 30) {
                    return {
                        display: value.slice(0, 30) + '...',
                        tooltip: value,
                    }
                }
                return value
        }
    }

    const sections = [
        {
            title: 'Basic Details',
            fields: [
                { label: 'Type', key: 'offer_category' },
                { label: 'Discount Code', key: 'coupon_code', className: '!uppercase' },
                { label: 'Offer Name', key: 'title' },
                { label: 'Offer Title', key: 'payment_offer_title' },
                { label: 'Offer Subtitle', key: 'payment_offer_sub_title' },
                { label: 'Active Period', key: 'period' },
                { label: 'View in Listing', key: 'view_in_listing' },
                {
                    label: 'Override Badge Text',
                    key: 'override_badge_text',
                    showIf: values.offer_category === OFFER_CATEGORY.CASHBACK,
                },
                { label: 'Badge Text', key: 'offer_title', showIf: values.override_badge_text === true },
            ],
        },
        values.offer_category === OFFER_CATEGORY.NO_COST_EMI
            ? null
            : {
                  title: 'Discount Details',
                  fields: [
                      { label: 'Discount Type', key: 'discount_type' },
                      { label: 'Discount Amount', key: 'amount', showIf: values.discount_type === 'fixed' },
                      {
                          label: 'Discount Percentage',
                          key: 'percentage',
                          showIf: values.discount_type === 'percentage',
                      },
                      {
                          label: 'Discount Capping Set',
                          key: 'limit_discount_amount',
                          showIf: values.discount_type === 'percentage',
                      },
                      {
                          label: 'Maximum Discount',
                          key: 'max_discount_amount',
                          showIf: values.limit_discount_amount === true && values.discount_type === 'percentage',
                      },
                  ],
              },
        {
            title: 'Usage & Cart Limits',
            fields: [
                { label: 'Usage Limit', key: 'max_coupon_count' },
                { label: 'Minimum Cart Value', key: 'min_cart' },
                { label: 'Maximum Cart Value', key: 'max_cart' },
            ],
        },
        {
            title: 'Payment Method Details',
            fields: [
                {
                    label: 'Payment Method',
                    key: 'payment_method_type',
                    showIf: values.offer_category !== OFFER_CATEGORY.NO_COST_EMI,
                },
                { label: 'Card Methods', key: 'cards', showIf: values.payment_method_type === 'cards' },
                { label: 'EMI Methods', key: 'emis', showIf: values.payment_method_type === 'emi' },
                { label: 'Wallets', key: 'wallets', showIf: values.payment_method_type === 'wallet' },
                { label: 'UPI Apps', key: 'psp_apps', showIf: values.payment_method_type === 'upi' },
                {
                    label: 'Bank Selection',
                    key: 'show_valid_banks',
                    showIf: ['cards', 'emi'].includes(values.payment_method_type),
                },
                {
                    label: 'Selected Banks',
                    key: 'valid_banks',
                    showIf:
                        ['cards', 'emi'].includes(values.payment_method_type) && values.show_valid_banks === 'specific',
                },
                {
                    label: 'BINs',
                    key: 'bins',
                    showIf: values.payment_method_type === 'cards',
                },
                {
                    label: 'Auto Apply',
                    key: 'auto_apply',
                    showIf: values.offer_category === OFFER_CATEGORY.PAYMENT_PARTNER,
                },
                {
                    label: 'Selected Banks',
                    key: 'valid_bank_codes',
                    showIf: values.offer_category === OFFER_CATEGORY.NO_COST_EMI,
                },
            ],
        },
        !isMerchantUser && values.merchants
            ? {
                  title: 'Merchant Selection',
                  fields: [{ label: 'Selected Merchants', key: 'merchants' }],
              }
            : null,
    ].filter(Boolean)

    return (
        <div className='flex flex-col gap-y-3'>
            <h3 className='my-0'>Summary</h3>
            {createdBy?.length ? (
                <div className='flex justify-between text-sm'>
                    <span className='text-gray-500'> Created By:</span>
                    <span className='font-medium capitalize max-w-[60%] text-right truncate'>
                        {createdBy.endsWith('@gokwik.co') ? 'GoKwik' : createdBy}
                    </span>
                </div>
            ) : null}
            {sections.map((section, index) => (
                <div key={index} className='flex flex-col gap-y-2'>
                    <h4 className='text-base font-medium m-0'>{section.title}</h4>
                    <div className='flex flex-col gap-y-1'>
                        {section.fields
                            .filter((field) => field.showIf === undefined || field.showIf)
                            .map((field, fieldIndex) => (
                                <div key={fieldIndex} className='flex justify-between text-sm'>
                                    <span className='text-gray-500'>{field.label}:</span>
                                    <span
                                        className={
                                            'font-medium capitalize max-w-[60%] text-right truncate' +
                                            ` ${field.className}`
                                        }
                                    >
                                        {(() => {
                                            const value = formatValue(field.key, values[field.key])
                                            if (typeof value === 'object' && value !== null && 'display' in value) {
                                                if (value.tooltip)
                                                    return (
                                                        <Tooltip title={value.tooltip}>
                                                            <span>{value.display}</span>
                                                        </Tooltip>
                                                    )
                                                if (value.popover)
                                                    return (
                                                        <Popover content={value.popover} title={value.title}>
                                                            <span>{value.display}</span>
                                                        </Popover>
                                                    )
                                            }
                                            return value
                                        })()}
                                    </span>
                                </div>
                            ))}
                    </div>
                </div>
            ))}
            <h4 className='text-base font-medium m-0'>Offer Preview</h4>
            <div className='flex flex-col items-start gap-y-1 p-3 rounded-md border border-dashed border-gray-300'>
                <div className='flex items-center gap-x-1 max-w-full'>
                    <img
                        className='w-7 h-7 bg-[#ddd] rounded-full'
                        src={values.logo_url || OfferSvg}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null
                            currentTarget.src = OfferSvg
                        }}
                        onLoad={({ currentTarget }) => {
                            currentTarget.style.padding = '0px'
                            if (currentTarget.src === OfferSvg) {
                                currentTarget.style.padding = '4px'
                            }
                        }}
                        alt='offer-icon'
                    />
                    {(values.offer_category === OFFER_CATEGORY.CASHBACK || !!values.auto_apply) &&
                    values.payment_offer_title ? (
                        <p className='max-w-[50%] truncate'>{values.payment_offer_title}</p>
                    ) : (
                        values.coupon_code && (
                            <Tooltip title={values.coupon_code}>
                                <span className='p-1 inline-block max-w-[50%] truncate rounded border border-dashed text-xs border-gray-400 uppercase'>
                                    {values.coupon_code}
                                </span>
                            </Tooltip>
                        )
                    )}
                    <span className='p-1 px-2 rounded bg-green-50 text-xs text-green-600'>Payment Offer</span>
                </div>
                {values.auto_apply && (
                    <div className='self-stretch capitalize text-xs text-green-600'>
                        <TagsFilled /> {values.payment_offer_sub_title || 'Auto applied with select payment method'}
                    </div>
                )}
                {((values.discount_type === 'fixed' && !isNaN(values.amount)) ||
                    (values.discount_type === 'percentage' && !isNaN(values.percentage))) && (
                    <div className='mt-1 text-green-600 text-xs'>
                        {values.offer_category === OFFER_CATEGORY.CASHBACK ? 'You Get' : 'You Save'}{' '}
                        {values.discount_type === 'fixed' ? currencySymbol + values.amount : null}
                        {values.discount_type === 'percentage'
                            ? `${values.percentage}%` +
                              (values.max_discount_amount ? ` Upto ${currencySymbol}${values.max_discount_amount}` : '')
                            : null}
                    </div>
                )}
                {values.tnc?.trim()?.length && (
                    <Button
                        type='link'
                        className='p-0 flex items-center text-xs font-normal underline text-[#004B8D] focus:outline-none'
                        onClick={() => setTncExpand(!tncExpand)}
                    >
                        See {tncExpand ? 'less' : 'more'} <DownOutlined rotate={tncExpand ? 180 : 0} />
                    </Button>
                )}
                {tncExpand && (
                    <div className='w-full flex flex-col gap-y-2 text-xs font-light text-gray-500'>
                        <Divider className='my-0' type='horizontal' dashed />
                        {values.tnc?.length && (
                            <ul className='list-disc ps-4'>
                                {values.tnc?.split('\n')?.map((tnc, i) => (
                                    <li key={i}>{tnc}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
})

Summary.displayName = 'Summary'

export default Summary
